import React, { useCallback, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import IconButton from "@mui/material/IconButton";
import { useMutation } from "@apollo/client/react";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import Tooltip from "@mui/material/Tooltip";

import { GENERATE_PROJECT_REPORT_MUTATION } from "../../constants/graphql";
import useNotification from "../../hooks/notification";
import SelectReceivingModal from "./SelectReceivingModal";
import { GENERATE_PROJECT_REPORT_BY_CLIENT_MUTATION } from "../../constants/graphql/mutations";
import { useBanner } from "../../contexts/banner";
import { adminConfiguration } from "./Page";
import useQueryParams from "../../hooks/useQueryParams";
import {
  buildReportGenerationVariables,
  shouldGenerateReportByClient,
} from "./reportGenerationVariables";

export default function GenerateReport({ entity, selectedClientId, selectedProjectId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useQueryParams();
  const item = entity;
  const [isClosing, setIsClosing] = useState(false);
  const openReceiving = queryParams.get("generateReportId") === item.id && !isClosing;

  // Reset isClosing when URL changes
  useEffect(() => {
    if (queryParams.get("generateReportId") !== item.id) {
      setIsClosing(false);
    }
  }, [location.search, item.id]);

  const { notifySuccess, notifyError } = useNotification();
  const { displayBanner, closeBanner } = useBanner();
  const [generateProjectReport] = useMutation(GENERATE_PROJECT_REPORT_MUTATION);
  const [generateProjectReportByClient] = useMutation(GENERATE_PROJECT_REPORT_BY_CLIENT_MUTATION);
  const isMultiProjectBillingReport = [
    "multi_project_billing",
    "multi_project_billing_roll_up_only",
    "multi_project_billing_operational_activity",
  ].includes(item.payload.report_type);

  const handleToggle = () => {
    const isCompanyWidePredefinedReport =
      item.payload.predefined &&
      (item.payload.company_wide || item.payload.name === "Active Customer List");

    if (isCompanyWidePredefinedReport) {
      handleSubmitReport({ projectId: null });
      return;
    }

    const currentParams = new URLSearchParams(window.location.search);
    if (openReceiving) {
      currentParams.delete("generateReportId");
    } else {
      currentParams.set("generateReportId", item.id);
    }
    currentParams.delete("projectId");
    currentParams.delete("startDate");
    currentParams.delete("endDate");

    const newSearch = currentParams.toString();
    const newPath = window.location.pathname + (newSearch ? `?${newSearch}` : '');
    navigate(newPath);
  };

  const handleClose = () => {
    setIsClosing(true);

    const currentParams = new URLSearchParams(window.location.search);
    currentParams.delete("generateReportId");
    currentParams.delete("projectId");
    currentParams.delete("startDate");
    currentParams.delete("endDate");
    
    const newSearch = currentParams.toString();
    const newPath = window.location.pathname + (newSearch ? `?${newSearch}` : '');
    navigate(newPath);
  };

  const handleOpenRecords = id => {
    const element = document.getElementById(
      `nested-${adminConfiguration.key}-${id}-${adminConfiguration.nestedTable.key}`
    );
    if (element?.getAttribute("data-open") === "false") {
      element.click();
    }
  };

  const handleSubmitReport = useCallback(
    async ({
      projectId,
      projectIds,
      clientId,
      startDate,
      endDate,
      inventoryAsOfDate,
      receivingNotes,
      warehouseId,
      costCenterId,
      assetFieldFilters,
    }) => {
      const variables = buildReportGenerationVariables({
        favoriteReportId: item.id,
        reportPayload: item.payload,
        baseFilters: item.payload.filters || [],
        modalValues: {
          projectId,
          projectIds,
          clientId,
          startDate,
          endDate,
          inventoryAsOfDate,
          receivingNotes,
          warehouseId,
          costCenterId,
          assetFieldFilters,
        },
      });
      displayBanner({
        message: "Generating file...",
        type: "report",
        ownerId: item.id,
        startedAt: Date.now(),
      });

      const generateReportQuery = shouldGenerateReportByClient(variables)
        ? generateProjectReportByClient({ variables })
        : generateProjectReport({ variables });

      return generateReportQuery
        .then(() => {
          handleClose();
          handleOpenRecords(item.id);
        })
        .catch(e => {
          console.log(e);
          closeBanner();
          notifyError("Something went wrong");
        });
    },
    [
      closeBanner,
      displayBanner,
      generateProjectReport,
      generateProjectReportByClient,
      handleClose,
      handleOpenRecords,
      item,
      notifyError,
    ]
  );

  return (
    <>
      <Tooltip arrow title="Generate Report">
        <IconButton onClick={handleToggle}>
          <AddCircleOutlineOutlinedIcon />
        </IconButton>
      </Tooltip>
      {openReceiving && (
        <SelectReceivingModal
          selectedClientId={selectedClientId}
          selectedProjectId={selectedProjectId}
          name={item.payload.name}
          title="Generate Report"
          description={`Template: ${item.payload.name}`}
          buttonLabel="Generate Report"
          onSubmit={handleSubmitReport}
          onClose={handleToggle}
          alwaysShowDates={
            item.payload.report_type === "shipments" ||
            item.payload.report_type === "inventory_requests" ||
            item.payload.report_type === "comprehensive" ||
            item.payload.report_type === "billing" ||
            isMultiProjectBillingReport
          }
          showReceivingNotes={!!item.payload.receiving_report}
          simpleValidation={true}
          reportType={item.payload.report_type}
          inventoryAsOfDate={item.payload.sub_type === "inventory_as_of_date"}
          reportMode={item.payload.mode}
          isPredefined={item.payload.predefined}
        />
      )}
    </>
  );
}
